import {
    CheckCircleIcon,
    XCircleIcon,
    WarningIcon,
    ClockIcon,
    InfoIcon,
} from '@phosphor-icons/react'
import styles from './ScoopStatusBubble.module.scss'

/**
 * ScoopStatusBubble - A status indicator component with icon and optional label
 *
 * @component
 * @param {Object} props - Component props
 * @param {'success' | 'error' | 'warning' | 'pending' | 'info'} props.status - Status type to display
 * @param {boolean} [props.withLabel=false] - Whether to show the status text label alongside the icon
 * @param {'small' | 'medium'} [props.size='medium'] - Bubble size (affects icon size and padding)
 * @param {string} [props.className] - Additional CSS classes to apply
 * @param {Object} [props.style] - Inline styles (for CSS custom properties only)
 * @param {...any} props - All other props are passed to the native HTML div element
 *
 * @example
 * // Basic usage - icon only
 * <ScoopStatusBubble status="success" />
 *
 * @example
 * // With label
 * <ScoopStatusBubble status="error" withLabel />
 *
 * @example
 * // Small size
 * <ScoopStatusBubble status="warning" size="small" />
 */
export const ScoopStatusBubble = ({
    status = 'pending',
    withLabel = false,
    size = 'medium',
    className = '',
    style,
    children,
    ...props
}) => {
    const statusLower = status?.toLowerCase()
    const hasCustomContent = Boolean(children)

    // Determine which status class to apply
    const getStatusClass = () => {
        switch (statusLower) {
            case 'success':
                return styles.statusSuccess
            case 'error':
            case 'failed':
                return styles.statusError
            case 'warning':
                return styles.statusWarning
            case 'info':
                return styles.statusInfo
            case 'pending':
            default:
                return styles.statusPending
        }
    }

    // Determine which icon to use
    const getStatusIcon = () => {
        const iconProps = {
            size: size === 'small' ? 14 : 16,
            weight: 'fill',
        }

        switch (statusLower) {
            case 'success':
                return <CheckCircleIcon {...iconProps} />
            case 'error':
            case 'failed':
                return <XCircleIcon {...iconProps} />
            case 'warning':
                return <WarningIcon {...iconProps} />
            case 'info':
                return <InfoIcon {...iconProps} />
            case 'pending':
            default:
                return <ClockIcon {...iconProps} />
        }
    }

    const bubbleClassName = [
        withLabel ? styles.statusBubbleWithLabel : styles.statusBubble,
        styles[size] || styles.medium,
        getStatusClass(),
        className,
    ]
        .filter(Boolean)
        .join(' ')

    const labelClassName = [styles.statusLabel, getStatusClass()].filter(Boolean).join(' ')

    return (
        <div className={bubbleClassName} style={style} {...props}>
            {hasCustomContent ? children : getStatusIcon()}
            {withLabel && !hasCustomContent && <span className={labelClassName}>{status}</span>}
        </div>
    )
}

export default ScoopStatusBubble
